import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MetaTags from '@/components/meta/MetaTags';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/use-user-roles';
import { supabase } from '@/integrations/supabase/client';
import { handleMediaUpload } from '@/utils/mediaHandlers';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import BulletinMasonry from '@/features/bulletins-test/BulletinMasonry';
import BulletinLightbox from '@/components/bulletins/BulletinLightbox';
import PostPromptModal from '@/components/bulletins/PostPromptModal';
import { TestBulletinItem } from '@/features/bulletins-test/TestBulletinCard';
import { useBulletinsFeedV2 } from '@/hooks/use-bulletins-feed-v2';
import { useBulletinForm } from '@/hooks/use-bulletin-form';

const BulletinsTest: React.FC = () => {
  const { user } = useAuth();
  const { isPremium, isAdmin } = useUserRoles();
  const params = useParams();
  const navigate = useNavigate();
  
  // Use bulletin form for consistent posting logic
  const bulletinForm = useBulletinForm(false, () => {
    setIsModalOpen(false);
    refresh(); // Refresh the feed after posting
  });

  // Extract URL parameters and set internal state accordingly
  const urlTab = params.tab || 'pour-toi';
  const urlCategory = params.category || 'all';
  const urlBulletinId = params.bulletinId;
  
  // Map URL parameters to internal state
  const initialMode = urlTab === 'chrono' || urlCategory === 'chrono' ? 'chrono' : 'all';
  const initialCategory = urlCategory === 'chrono' ? 'all' : urlCategory;

  // Optional tag filter coming from header search
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [mode, setMode] = useState<'all' | 'chrono'>(initialMode);
  const [category, setCategory] = useState<string>(initialCategory);

  // ALL = full archive shuffled; Chrono = newest-first
  const BACKFILL_14D_FOR_ALL = false;

  // Lightweight feed hook focused on mobile layout and manual refresh
  // Include 'pending' on test page so new uploads appear immediately
  const MOD_STATUSES = useMemo(() => ['approved', 'flagged', 'pending'] as const, []);

  const { items, loading, hasMore, loadMore, refresh, empty } = useBulletinsFeedV2({
    // Always fetch full archive chronologically; UI decides ALL vs Chrono behavior
    mode: 'bulletins',
    category,
    pageSize: 24,
    timeWindow: 'all',
    moderationStatuses: MOD_STATUSES as unknown as ('approved' | 'flagged' | 'pending')[],
    includeNullModeration: true,
  });

  const [likedMap, setLikedMap] = useState<Record<string, boolean>>({});
  const [selectedItem, setSelectedItem] = useState<TestBulletinItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [postTitle, setPostTitle] = useState('');
  const [postDescription, setPostDescription] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [optimisticPosts, setOptimisticPosts] = useState<TestBulletinItem[]>([]);
  const [isPosting, setIsPosting] = useState(false);
  const [showPostSuccess, setShowPostSuccess] = useState(false);
  const postSuccessTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track displayed order to prevent re-shuffling on load more
  const displayedOrderRef = useRef<string[]>([]);

  // Measure content area to respect left navigation on desktop
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentBounds, setContentBounds] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  
  useEffect(() => {
    const update = () => {
      if (!contentRef.current) return;
      const r = contentRef.current.getBoundingClientRect();
      setContentBounds({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Update internal state when URL params change
  useEffect(() => {
    const newMode = urlTab === 'chrono' || urlCategory === 'chrono' ? 'chrono' : 'all';
    const newCategory = urlCategory === 'chrono' ? 'all' : urlCategory;
    
    if (newMode !== mode) {
      setMode(newMode);
    }
    if (newCategory !== category) {
      setCategory(newCategory);
    }
  }, [urlTab, urlCategory, mode, category]);

  const categories = useMemo(() => {
    const s = new Set<string>();
    (items || []).forEach((it: any) => {
      // Add auto_category (highest confidence from Vision API)
      if (it.auto_category) {
        s.add(it.auto_category);
      }
      // Add user_category if manually set
      if (it.user_category) {
        s.add(it.user_category);
      }
      // Add top 2 labels from content_labels for more variety
      if (Array.isArray(it.content_labels) && it.content_labels.length > 0) {
        // Take first 2 labels (Vision API returns them sorted by confidence)
        it.content_labels.slice(0, 2).forEach((label: any) => {
          if (label && typeof label === 'string') {
            s.add(label.toLowerCase());
          }
        });
      }
    });
    // Randomize category order for variety
    const arr = Array.from(s);
    return arr.sort(() => Math.random() - 0.5);
  }, [items]);

  // Display list: shuffle for "All" mode, chronological for "Chrono" mode
  const displayedItems = useMemo(() => {
    const bulletins = (items || []) as TestBulletinItem[];
    
    if (mode === 'all') {
      // Preserve order of already-displayed items, only shuffle new ones
      const existingOrder = displayedOrderRef.current;
      const bulletinMap = new Map(bulletins.map(b => [b.id, b]));
      
      // Separate existing (already displayed) from new items
      const existing: TestBulletinItem[] = [];
      const newItems: TestBulletinItem[] = [];
      
      // Keep existing items in their current order
      existingOrder.forEach(id => {
        const item = bulletinMap.get(id);
        if (item) {
          existing.push(item);
          bulletinMap.delete(id);
        }
      });
      
      // Remaining items are new - shuffle them
      newItems.push(...bulletinMap.values());
      for (let i = newItems.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newItems[i], newItems[j]] = [newItems[j], newItems[i]];
      }
      
      // Combine: existing items first (maintain scroll position), then new shuffled items
      const result = [...existing, ...newItems];
      
      // Update the displayed order ref
      displayedOrderRef.current = result.map(b => b.id);
      
      return result;
    }
    
    // Chrono mode: keep chronological order and update ref
    displayedOrderRef.current = bulletins.map(b => b.id);
    return bulletins;
  }, [items, mode]);

  // Load existing likes when user or displayed items change
  useEffect(() => {
    if (!user?.id || displayedItems.length === 0) return;

    const loadExistingLikes = async () => {
      try {
        const bulletinIds = displayedItems.map(item => item.id);
        const { data, error } = await supabase
          .from('bulletin_likes')
          .select('bulletin_id')
          .eq('user_id', user.id)
          .in('bulletin_id', bulletinIds);

        if (error) throw error;

        const newLikedMap: Record<string, boolean> = {};
        data?.forEach(like => {
          newLikedMap[like.bulletin_id] = true;
        });

        setLikedMap(newLikedMap);
      } catch (error) {
        console.error('Failed to load existing likes:', error);
      }
    };

    loadExistingLikes();
  }, [user?.id, displayedItems]);

  // Handle URL bulletin ID after displayedItems is available
  useEffect(() => {
    if (urlBulletinId && displayedItems.length > 0) {
      const foundItem = displayedItems.find(item => item.id === urlBulletinId);
      if (foundItem) {
        setSelectedItem(foundItem);
      }
    } else if (!urlBulletinId && selectedItem) {
      setSelectedItem(null);
    }
  }, [urlBulletinId, displayedItems, selectedItem]);

  const handleModeChange = useCallback((next: 'all' | 'chrono') => {
    setMode(next);
    // Reset category filter when switching modes
    setCategory('all');
    // Clear displayed order to allow fresh shuffle/sort
    displayedOrderRef.current = [];
    // Update URL when mode changes
    const newTab = next === 'chrono' ? 'chrono' : 'pour-toi';
    navigate(`/bulletins/${newTab}/all`, { replace: true });
  }, [navigate]);

  const handleLike = useCallback(async (id: string) => {
    // Optimistic like
    setLikedMap((prev) => ({ ...prev, [id]: true }));
    try {
      const { error } = await supabase
        .from('bulletin_likes')
        .insert([{ bulletin_id: id, user_id: user?.id }]);
      if (error && error.code !== '23505') throw error;
    } catch (e) {
      console.error('Like failed:', e);
      setLikedMap((prev) => ({ ...prev, [id]: false }));
      toast.error('Failed to like');
    }
  }, [user?.id]);

  const handleOpen = useCallback((item: TestBulletinItem) => {
    setSelectedItem(item);
    // Update URL to include bulletin ID
    const currentTab = mode === 'chrono' ? 'chrono' : 'pour-toi';
    navigate(`/bulletins/${currentTab}/${category}/${item.id}`, { replace: true });
  }, [mode, category, navigate]);

  const handleDelete = useCallback(async (_id: string) => {
    try {
      await refresh();
    } catch (e) {
      console.error('Post-delete refresh failed', e);
    }
  }, [refresh]);

  const handleRefresh = useCallback(() => {
    refresh();
    setOptimisticPosts([]);
  }, [refresh]);

  const handleSubmitPost = useCallback(
    async (
      postCopy: string,
      mediaTitle: string,
      mediaDescription: string,
      image?: File | null,
      isArt?: boolean,
      disabledCommunityIds?: Set<string>
    ) => {
      // Combine title and description for all posts (both text-only and media)
      let content = postCopy || '';
      if (mediaDescription?.trim()) {
        content = content ? `${content}\n\n${mediaDescription}` : mediaDescription;
      }
      
      try {
        // Show posting state
        setIsPosting(true);
        setIsModalOpen(false); // Close the modal immediately
        
        // Set the artstream flag based on toggle
        if (isArt !== undefined) {
          bulletinForm.setIsArtstream(isArt);
        }
        
        // Store disabled communities for community extraction
        if (disabledCommunityIds) {
          bulletinForm.setDisabledCommunities(disabledCommunityIds);
        }
        
        // Timeout protection: Ensure posting never hangs forever
        const uploadPromise = bulletinForm.handleSubmit(
          null, 
          undefined, 
          content, 
          image || undefined, 
          undefined, // directLoopsContent
          disabledCommunityIds // overrideDisabledCommunities - critical for respecting toggles
        );
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Upload timed out after 5 minutes')), 300000) // 5 minutes for slow connections
        );
        
        // Race between upload and timeout
        const success = await Promise.race([uploadPromise, timeoutPromise]) as boolean;
        
        if (success) {
          // Show success state
          setShowPostSuccess(true);
          
          // Auto-hide success message after 3 seconds
          if (postSuccessTimeoutRef.current) {
            clearTimeout(postSuccessTimeoutRef.current);
          }
          
          postSuccessTimeoutRef.current = setTimeout(() => {
            setShowPostSuccess(false);
          }, 3000);
          
          // Feed will refresh via onSuccess callback
        }
      } catch (error) {
        console.error('Error posting to bulletins:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error('Failed to post', {
          description: errorMessage.includes('timed out') 
            ? 'Upload timed out after 5 minutes. Please check your connection and try again.'
            : 'There was an error posting your content. Please try again.',
        });
      } finally {
        setIsPosting(false);
      }
    },
    [bulletinForm]
  );

  const handleNavigate = useCallback(
    (id: string) => {
      const next = displayedItems.find((it) => it.id === id) || null;
      setSelectedItem(next);
    },
    [displayedItems]
  );

  // Infinite scroll: load more when sentinel comes into view
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Only trigger if visible, not already loading, and has more content
        if (entries[0].isIntersecting && !isLoadingRef.current && !loading && hasMore) {
          isLoadingRef.current = true;
          loadMore();
        }
      },
      {
        threshold: 0,
        rootMargin: '200px', // Reduced from 400px to prevent premature loading
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  // Reset loading ref when loading completes
  useEffect(() => {
    if (!loading) {
      isLoadingRef.current = false;
    }
  }, [loading]);

  return (
    <div ref={contentRef}>
      <MetaTags
        title="Bulletins – ALL & Chrono"
        description="Mobile-focused test feed with ALL (random) and Chrono (newest) modes."
        image="/og-image.png"
        url={window.location.href}
      />

      <PageLayout
        showComposer={true}
        hidePostButtons={true}
        mode={mode}
        onModeChange={handleModeChange}
        categories={categories}
        category={category}
        onCategoryChange={(c) => {
          setCategory(c);
          // Clear displayed order to allow fresh shuffle/sort when category changes
          displayedOrderRef.current = [];
          // Update URL when category changes
          const currentTab = mode === 'chrono' ? 'chrono' : 'pour-toi';
          navigate(`/bulletins/${currentTab}/${c}`, { replace: true });
        }}
        showRefresh={empty}
        onRefresh={refresh}
        onTagSelect={(tag) => setTagFilter(tag)}
        onCompose={() => setIsModalOpen(true)}
      >
        <section className="pb-6">
          <BulletinMasonry
            key={`${mode}-${category}`}
            items={displayedItems}
            likedMap={likedMap}
            onLike={handleLike}
            onOpen={handleOpen}
            onDelete={handleDelete}
            uploadProgress={undefined}
          />

          {/* Infinite scroll sentinel - always present when hasMore to prevent jumps */}
          {hasMore && (
            <div ref={loadMoreRef} className="py-4 text-center text-sm text-muted-foreground" style={{ minHeight: '60px' }}>
              {loading ? 'Loading…' : ''}
            </div>
          )}
        </section>
      </PageLayout>

      {selectedItem && (
        <BulletinLightbox
          bulletins={displayedItems as any}
          selectedId={selectedItem.id}
          onNavigate={(id) => {
            const next = (displayedItems as any[]).find((it) => it.id === id) || null;
            setSelectedItem(next);
            // Update URL to keep in sync with navigation
            const currentTab = mode === 'chrono' ? 'chrono' : 'pour-toi';
            navigate(`/bulletins/${currentTab}/${category}/${id}`, { replace: true });
          }}
          onClose={() => {
            setSelectedItem(null);
            // Update URL to remove bulletin ID
            const currentTab = mode === 'chrono' ? 'chrono' : 'pour-toi';
            navigate(`/bulletins/${currentTab}/${category}`, { replace: true });
          }}
          contentBounds={contentBounds}
          preloadedThumbnail={selectedItem.thumbnail_url || selectedItem.image_url}
        />
      )}

      {/* Success notification */}
      <AnimatePresence>
        {showPostSuccess && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-green-500/90 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 max-w-sm w-full"
          >
            <CheckCircle className="h-5 w-5 text-white" />
            <div>
              <h4 className="font-medium">Post published!</h4>
              <p className="text-sm text-white/90">Your content has been posted to Artstream</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <PostPromptModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmitPost}
        isPremium={isPremium}
        isAdmin={isAdmin}
        uploadProgress={undefined}
      />
    </div>
  );
};

export default BulletinsTest;